/**
 * WHAT THE MACHINE SAID ABOUT ITSELF, off the installer's output.
 *
 * `install-device.sh` probes the device over adb and emits ONE line:
 *
 *     ::device-facts:: {"model":"TC-TOUCH-K10","ramMb":3829, …}
 *
 * Deliberately separate from the human `✓ identity: …` line beside it. That
 * one is for an operator watching the log and is free to be reworded; a UI
 * that scrapes prose breaks the day somebody improves the wording. This is a
 * contract, and it is versioned by being additive — a field this parser does
 * not know is ignored, never fatal.
 *
 * PURITY CONTRACT: no React, no network. Everything here is testable against a
 * string.
 */

/** Exactly the shape `DeviceUnitSchema.hardware` stores, minus `capturedAt`. */
export interface DeviceFacts {
  serial?: string;
  model?: string;
  brand?: string;
  osVersion?: string;
  sdkInt?: number;
  cpuAbi?: string;
  /** `ro.build.fingerprint` — the ODM's own build id, the strong identifier. */
  fingerprint?: string;
  cpuModel?: string;
  cpuCores?: number;
  cpuMaxMhz?: number;
  ramMb?: number;
  storageTotalMb?: number;
  storageFreeMb?: number;
  wifiMac?: string;
  ethernetMac?: string;
  displayCount?: number;
  usbPeripherals?: string[];
  /**
   * 🔴 IS THIS UNIT UNDER OUR MANAGEMENT? — `dpm list-owners` via the installer.
   *
   * `true` = a Device Owner is set, so the fleet can restart, lock down and
   * wipe this till remotely. `false` = it cannot, ever: Device Owner can only
   * be provisioned on a device with no accounts that has not finished setup,
   * and can only be removed by a factory reset. There is no later enrolment.
   *
   * `undefined` is THE THIRD STATE and is not a synonym for `false`. An older
   * installer emits no such key, and a probe that could not ask must never be
   * rendered as "NOT managed" — that is the absence-of-evidence mistake this
   * page has already made twice. Read it through `managementState()`, never as
   * a bare truthiness test.
   */
  deviceOwner?: boolean;
  /** The package holding Device Owner, when one does — e.g. `com.rubytill.pos`. */
  deviceOwnerPackage?: string;
}

/**
 * Managed, not managed, or never asked — the only three honest answers.
 *
 * `unknown` covers a device we have not probed at all AND a probe run by an
 * installer too old to report the field. Both mean "we do not know", which is
 * a different thing from "we know it is unmanaged" and must never be shown as
 * a fault.
 */
export type ManagementState = 'managed' | 'unmanaged' | 'unknown';

export function managementState(f: DeviceFacts | null | undefined): ManagementState {
  if (!f || typeof f.deviceOwner !== 'boolean') return 'unknown';
  return f.deviceOwner ? 'managed' : 'unmanaged';
}

const MARKER = '::device-facts::';

const str = (v: unknown, max = 256): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t.slice(0, max) : undefined;
};

/**
 * STRICTLY A JSON BOOLEAN. `"true"`, `1` and `"yes"` all come back `undefined`
 * — i.e. UNKNOWN — rather than being coerced. Coercing here would let a
 * malformed probe answer a question it never actually asked, and the answer it
 * would invent ("managed") is the dangerous one.
 */
const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);

const num = (v: unknown): number | undefined => {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return undefined;
  return Math.round(v);
};

/**
 * Pull the facts out of installer output.
 *
 * Takes the LAST marker in the text, not the first: a retry appends a second
 * run's output to the same buffer, and the older block describes the device as
 * it was before this run touched it.
 *
 * Returns `null` when there is no marker or its JSON is unreadable — which is
 * every install by an older copy of the script, and must therefore be an
 * ordinary outcome rather than an error. Every field is validated
 * individually, so one bad value costs that value and not the block.
 */
export function parseDeviceFacts(output: string): DeviceFacts | null {
  if (!output) return null;
  const idx = output.lastIndexOf(MARKER);
  if (idx < 0) return null;
  const line = output.slice(idx + MARKER.length).split('\n')[0].trim();
  if (!line) return null;

  let raw: any;
  try {
    raw = JSON.parse(line);
  } catch {
    // A truncated line — the marker arrived in one stream chunk and the JSON
    // in the next. The caller re-parses the whole accumulated buffer on every
    // chunk, so the next one succeeds; a throw here would take the log view
    // down mid-install.
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;

  const usb = Array.isArray(raw.usbPeripherals)
    ? raw.usbPeripherals.map((x: unknown) => str(x)).filter(Boolean).slice(0, 64) as string[]
    : undefined;

  const facts: DeviceFacts = {
    serial: str(raw.serial, 128),
    model: str(raw.model, 128),
    brand: str(raw.brand, 128),
    osVersion: str(raw.osVersion, 64),
    sdkInt: num(raw.sdkInt),
    cpuAbi: str(raw.cpuAbi, 64),
    fingerprint: str(raw.fingerprint, 256),
    cpuModel: str(raw.cpuModel, 128),
    cpuCores: num(raw.cpuCores),
    cpuMaxMhz: num(raw.cpuMaxMhz),
    ramMb: num(raw.ramMb),
    storageTotalMb: num(raw.storageTotalMb),
    storageFreeMb: num(raw.storageFreeMb),
    wifiMac: str(raw.wifiMac, 64),
    ethernetMac: str(raw.ethernetMac, 64),
    displayCount: num(raw.displayCount),
    usbPeripherals: usb && usb.length ? usb : undefined,
    deviceOwner: bool(raw.deviceOwner),
    deviceOwnerPackage: str(raw.deviceOwnerPackage, 256),
  };

  // Drop the keys that came back empty so "did not learn it" stays absent
  // rather than becoming `undefined` on a JSON body, which is dropped on the
  // wire anyway and would make a merge keep a stale value.
  for (const k of Object.keys(facts) as (keyof DeviceFacts)[]) {
    if (facts[k] === undefined) delete facts[k];
  }
  return Object.keys(facts).length ? facts : null;
}

/** Wi-Fi when there is one, wired otherwise — what the `mac` column means. */
export function primaryMac(f: DeviceFacts | null | undefined): string | undefined {
  return f?.wifiMac || f?.ethernetMac;
}

/** `3829` -> `3.7 GB`. Bare MB under a gigabyte, because `0.4 GB` reads as nothing. */
export function formatMb(mb?: number): string {
  if (!mb || mb <= 0) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

/** `2200` -> `2.20 GHz`. */
export function formatMhz(mhz?: number): string {
  if (!mhz || mhz <= 0) return '—';
  return `${(mhz / 1000).toFixed(2)} GHz`;
}

/**
 * USB VENDOR IDS WE ACTUALLY RECOGNISE.
 *
 * `0fe6:811e` tells an operator nothing. "Thermal printer" does — and more to
 * the point, the three printer VIDs here are the EXACT ones
 * `PosPrinterPlugin.findPrinter()` probes for, so a device showing one is a
 * device this app can drive a printer on. That is the question somebody is
 * really asking when they look at this list.
 *
 * Deliberately short. A guessed label is worse than a raw id: "Card reader"
 * against something that is a cable adapter sends somebody down the wrong
 * path, while `214b:7260` is at least honestly unknown and can be searched.
 */
const USB_VENDORS: Record<string, string> = {
  '4b43': 'Thermal printer',
  '0fe6': 'Thermal printer',
  '0416': 'Thermal printer',
  '0eef': 'Touchscreen',
  '1a40': 'USB hub',
  '0e8d': 'MediaTek controller',
};

/**
 * `0fe6:811e` -> `Thermal printer · 0fe6:811e`, or the bare id when unknown.
 * Also accepts a whole lsusb line, for records captured before the installer
 * started storing bare ids.
 */
export function describeUsb(entry: string): string {
  const m = entry.match(/([0-9a-f]{4}):([0-9a-f]{4})/i);
  if (!m) return entry;
  const id = `${m[1].toLowerCase()}:${m[2].toLowerCase()}`;
  const name = USB_VENDORS[m[1].toLowerCase()];
  return name ? `${name} · ${id}` : id;
}

/**
 * Does this device have a printer THIS APP can drive?
 *
 * Matches `PosPrinterPlugin.findPrinter()`'s vendor list exactly. Answers a
 * real support question — "the printer does not work" — from the record,
 * rather than from somebody going back to the shop.
 */
const PRINTER_VIDS = ['4b43', '0fe6', '0416'];

export function hasKnownPrinter(f: DeviceFacts | null | undefined): boolean {
  return (f?.usbPeripherals || []).some((u) => {
    const m = u.match(/([0-9a-f]{4}):[0-9a-f]{4}/i);
    return !!m && PRINTER_VIDS.includes(m[1].toLowerCase());
  });
}
